import type { CSSProperties } from 'react';
import { SheetFrame } from '../design-system';
import { SleekButton } from '../theme';
import { ShippingCountdownHeader } from './ShippingCountdownHeader';
import { ShippingPositions } from './ShippingPositions';
import type { ShippingModalProps } from './ShippingModal';

const FALLBACK_TITLE_ID = 'shipping-sheet-title';

// Dark defaults for the shared countdown and fee parts. They sit under the
// `--co-ShippingModal-*` knobs, so an operator's theme still wins. The cast
// is safe: custom properties are valid inline style keys.
const DARK_DEFAULTS = {
  '--ship-default-countdownLabelColor': 'rgba(255,255,255,0.6)',
  '--ship-default-chipFill': 'rgba(255,255,255,0.08)',
  '--ship-default-chipText': '#FFFFFF',
  '--ship-default-feeCardBorder': 'rgba(255,255,255,0.08)',
  '--ship-default-feeCardFill': 'transparent',
  '--ship-default-rowLabelColor': 'rgba(255,255,255,0.5)',
  '--ship-default-rowValueColor': '#FFFFFF',
  '--ship-default-rowDivider': 'rgba(255,255,255,0.08)',
} as CSSProperties;

/**
 * Sleek's shipping sheet (partner: "a sheet like the new Odds"): the same
 * sections as `ShippingModal` on a dark bottom sheet, with both actions
 * inside it — the white primary (Confirm Shipping / Add Delivery) and the
 * dark secondary (Sell • N% Value).
 */
export function SleekShippingSheet({
  onClose,
  countdown,
  title,
  description,
  positions,
  total,
  note,
  primary,
  secondary,
  ariaLabelledBy,
}: ShippingModalProps) {
  const titleId = ariaLabelledBy ?? FALLBACK_TITLE_ID;
  return (
    <SheetFrame
      onClose={onClose}
      ariaLabelledBy={titleId}
      inspectName="ShippingModal"
      panelClassName="bg-[var(--co-ShippingModal-cardFill,#0E0F11)] [border-top-left-radius:var(--co-ShippingModal-sheetRadius,24px)] [border-top-right-radius:var(--co-ShippingModal-sheetRadius,24px)]"
      handleClassName="bg-[var(--co-ShippingModal-handle,#2F3037)]"
    >
      <section id={titleId} className="flex flex-col gap-5" style={DARK_DEFAULTS}>
        {countdown && <ShippingCountdownHeader countdown={countdown} />}

        {(title || description) && (
          <div className="flex flex-col gap-2">
            {title && (
              <h2
                className="text-[22px] font-bold leading-[27px] tracking-[-0.4px]"
                style={{ color: 'var(--co-ShippingModal-titleColor, #FFFFFF)' }}
              >
                {title}
              </h2>
            )}
            {description && (
              <p
                className="text-[15px] leading-[21px]"
                style={{ color: 'var(--co-ShippingModal-descColor, rgba(255,255,255,0.6))' }}
              >
                {description}
              </p>
            )}
          </div>
        )}

        {positions && positions.length > 0 && <ShippingPositions positions={positions} {...(total ? { total } : {})} />}

        {note && (
          <p
            className="text-[15px] leading-[21px]"
            style={{ color: 'var(--co-ShippingModal-noteColor, rgba(255,255,255,0.6))' }}
          >
            {note}
          </p>
        )}

        <div className="mt-1 flex flex-col gap-3">
          <SleekButton
            name="ShippingPrimaryButton"
            onClick={primary.onClick}
            disabled={(primary.disabled ?? false) || (primary.loading ?? false)}
            aria-busy={primary.loading || undefined}
            className="w-full"
          >
            {primary.label}
          </SleekButton>
          {secondary && (
            <SleekButton
              name="ShippingSecondaryButton"
              onClick={secondary.onClick}
              disabled={(secondary.disabled ?? false) || (secondary.loading ?? false)}
              aria-busy={secondary.loading || undefined}
              className="w-full"
            >
              {secondary.label}
            </SleekButton>
          )}
        </div>
      </section>
    </SheetFrame>
  );
}
